import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api, apiError } from "../lib/api";
import { toast } from "sonner";
import { Lock, Download } from "lucide-react";
import ComparisonView from "../components/ComparisonView";
import ConversationStarters from "../components/ConversationStarters";

export default function CalibrationComparison() {
  const { id } = useParams();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [locked, setLocked] = useState(false);
  const [exporting, setExporting] = useState(false);
  const pdfRef = useRef(null);

  const load = () => api.get(`/calibrations/${id}/comparison`).then((r) => setData(r.data)).catch((e) => {
    if (e.response?.status === 403) setLocked(true);
    else toast.error(apiError(e, "Failed to load"));
  });

  useEffect(() => { load(); }, [id]);

  // Poll until AI starters are ready
  useEffect(() => {
    if (!data || data.conversation_starters_ready) return;
    const t = setInterval(load, 4000);
    return () => clearInterval(t);
  }, [data]);

  const exportPdf = async () => {
    if (!pdfRef.current) return;
    setExporting(true);
    try {
      const [{ default: html2canvas }, { default: jsPDF }] = await Promise.all([
        import("html2canvas"),
        import("jspdf"),
      ]);
      const el = pdfRef.current;
      // Higher scale for readability; white bg to match card
      const canvas = await html2canvas(el, {
        scale: 2,
        backgroundColor: "#FFFFFF",
        useCORS: true,
        logging: false,
        windowWidth: el.scrollWidth,
      });
      const imgData = canvas.toDataURL("image/png");
      const pdf = new jsPDF({ unit: "pt", format: "a4", orientation: "portrait" });
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const margin = 24;
      const imgWidth = pageWidth - margin * 2;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      let heightLeft = imgHeight;
      let position = margin;
      pdf.addImage(imgData, "PNG", margin, position, imgWidth, imgHeight, undefined, "FAST");
      heightLeft -= pageHeight - margin * 2;
      while (heightLeft > 0) {
        pdf.addPage();
        position = margin - (imgHeight - heightLeft);
        pdf.addImage(imgData, "PNG", margin, position, imgWidth, imgHeight, undefined, "FAST");
        heightLeft -= pageHeight - margin * 2;
      }
      const safeTitle = (data?.title || "calibration").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");
      const filename = `calibrate-${safeTitle}-${id.slice(0, 8)}.pdf`;
      // Iframed previews (e.g. Emergent's in-IDE preview) block <a download>.
      // Detect iframe and fall back to opening in a new tab where the user can save.
      const inIframe = (() => { try { return window.self !== window.top; } catch { return true; } })();
      if (inIframe) {
        const blob = pdf.output("blob");
        const url = URL.createObjectURL(blob);
        const win = window.open(url, "_blank");
        if (!win) {
          // Popup blocker — offer a visible download link as toast action
          toast.success("PDF ready", {
            description: "Click to open — your browser blocked the auto-download.",
            action: { label: "Open PDF", onClick: () => window.open(url, "_blank") },
            duration: 15000,
          });
        } else {
          toast.success("PDF opened in a new tab — use your browser to save.");
        }
        setTimeout(() => URL.revokeObjectURL(url), 120000);
      } else {
        pdf.save(filename);
        toast.success("PDF downloaded");
      }
    } catch (err) {
      console.error(err);
      toast.error("PDF export failed");
    } finally {
      setExporting(false);
    }
  };

  if (locked) {
    return (
      <div className="max-w-2xl mx-auto px-6 py-16">
        <div className="cal-card p-10 text-center relative overflow-hidden">
          <div className="locked-pattern" />
          <Lock className="mx-auto mb-4" size={40} />
          <h2 className="font-display font-bold text-3xl mb-2">Vault sealed</h2>
          <p className="text-[#666] mb-6">The comparison unlocks once both employee and manager have submitted.</p>
          <button onClick={() => nav(`/calibrations/${id}`)} className="cal-btn" data-testid="locked-back">← Back</button>
        </div>
      </div>
    );
  }

  if (!data) return <div className="p-10 font-mono-cal text-xs">[LOADING…]</div>;

  return (
    <div className="max-w-6xl mx-auto px-6 md:px-10 py-12">
      <div className="flex justify-end mb-4">
        <button onClick={exportPdf} disabled={exporting} className="cal-btn cal-btn-ghost" data-testid="export-pdf-btn">
          <Download size={14} /> {exporting ? "Rendering…" : "Export PDF"}
        </button>
      </div>
      <div ref={pdfRef} style={{ background: "#FFFFFF", padding: 24 }}>
        <ComparisonView data={data} />
      </div>
      <ConversationStarters
        calibrationId={id}
        initialStarters={data.conversation_starters || []}
        ready={!!data.conversation_starters_ready}
      />
      <div className="mt-10 flex justify-end">
        <button onClick={() => nav(`/calibrations/${id}`)} className="cal-btn cal-btn-ghost" data-testid="comparison-back">← Back</button>
      </div>
    </div>
  );
}
